// AssoGroupes.jsx — Gestion des groupes (bureau) : groupes, éducateurs, joueurs, parents.
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Plus, X, Loader2, Users, MapPin, Clock, Pencil, Trash2, UserPlus,
  Link2, Baby, GraduationCap, Check,
} from 'lucide-react';

const ROLE_LABELS = { educateur: 'Éducateur', joueur: 'Joueur', parent: 'Parent' };

export default function AssoGroupes() {
  const [groupes, setGroupes] = useState([]);
  const [educs, setEducs] = useState([]);
  const [joueurs, setJoueurs] = useState([]);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // group | 'new' | null
  const [quick, setQuick] = useState(false);

  const load = async () => {
    try {
      const [g, ec, jc, pr] = await Promise.all([
        api.get('/groupes'),
        api.get('/groupes/candidats', { params: { role: 'educateur' } }),
        api.get('/groupes/candidats', { params: { role: 'joueur' } }),
        api.get('/groupes/parents'),
      ]);
      setGroupes(g.data);
      setEducs(ec.data);
      setJoueurs(jc.data);
      setParents(pr.data.parents);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const removeGroupe = async (g) => {
    if (!window.confirm(`Supprimer le groupe « ${g.nom} » ? Les joueurs ne seront pas supprimés.`)) return;
    try { await api.delete(`/groupes/${g.id}`); toast.success('Groupe supprimé'); load(); }
    catch { toast.error('Suppression impossible'); }
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="asso-groupes">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--theme-primary)' }}>Encadrement</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A] leading-[1.02]">
            {groupes.length} <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>groupe{groupes.length > 1 ? 's' : ''}.</span>
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setQuick(true)} className="btn-secondary" data-testid="quick-create-member-btn">
            <UserPlus className="w-4 h-4" /> Créer un membre
          </button>
          <button onClick={() => setEditing('new')} className="btn-primary" data-testid="new-groupe-btn">
            <Plus className="w-4 h-4" /> Nouveau groupe
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-44 rounded-2xl bg-white border border-[#E2E8F0] animate-pulse" />)}</div>
      ) : groupes.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-[#E2E8F0] rounded-2xl p-12 text-center" data-testid="groupes-empty">
          <div className="w-14 h-14 rounded-2xl bg-[#F1F5F9] grid place-items-center mx-auto mb-3 text-[#94A3B8]"><Users className="w-7 h-7" /></div>
          <h3 className="font-display font-black text-[#0F172A]">Aucun groupe</h3>
          <p className="text-sm text-[#475569] mt-1 mb-4">Créez votre premier groupe et affectez-y éducateurs et joueurs.</p>
          <button onClick={() => setEditing('new')} className="btn-primary mx-auto"><Plus className="w-4 h-4" /> Nouveau groupe</button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="groupes-list">
          {groupes.map((g) => (
            <div key={g.id} className="bg-white border-2 border-[#E2E8F0] rounded-2xl p-5" data-testid={`groupe-item-${g.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-display font-black text-xl text-[#0F172A]">{g.nom}</h3>
                {g.categorie && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full text-white shrink-0" style={{ background: 'var(--theme-primary)' }}>{g.categorie}</span>}
              </div>
              <div className="space-y-1.5 text-sm text-[#475569]">
                <div className="flex items-center gap-2"><Users className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} /> {g.nb_joueurs} joueur·s</div>
                {(g.jour || g.heure) && <div className="flex items-center gap-2"><Clock className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} /> {g.jour} {g.heure}</div>}
                {g.lieu && <div className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} /> {g.lieu}</div>}
                <div className="flex items-center gap-2"><GraduationCap className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} /> {g.educateurs?.length ? g.educateurs.map((e) => e.prenom + ' ' + e.nom).join(', ') : 'Aucun éducateur'}</div>
              </div>
              <div className="mt-4 pt-3 border-t border-[#F1F5F9] flex gap-2">
                <button onClick={() => setEditing(g)} className="btn-secondary !text-xs flex-1" data-testid={`edit-groupe-${g.id}`}><Pencil className="w-3 h-3" /> Gérer</button>
                <button onClick={() => removeGroupe(g)} className="btn-secondary !text-xs !text-[#EF4444]" data-testid={`delete-groupe-${g.id}`}><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Parents & enfants */}
      <ParentsSection parents={parents} joueurs={joueurs} onChange={load} />

      {editing && (
        <GroupeModal
          group={editing === 'new' ? null : editing}
          educs={educs}
          joueurs={joueurs}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {quick && <QuickMemberModal onClose={() => setQuick(false)} onSaved={() => { setQuick(false); load(); }} />}
    </div>
  );
}

// ─────────────── Modal groupe (création + gestion effectif) ───────────────
function GroupeModal({ group, educs, joueurs, onClose, onSaved }) {
  const isNew = !group;
  const [form, setForm] = useState({
    nom: group?.nom || '', categorie: group?.categorie || '',
    jour: group?.jour || '', heure: group?.heure || '', lieu: group?.lieu || '',
  });
  const [educIds, setEducIds] = useState(group?.educateur_ids || []);
  const [detail, setDetail] = useState(null); // {joueurs:[...]} for existing group
  const [saving, setSaving] = useState(false);

  const reloadDetail = async (gid) => {
    const { data } = await api.get(`/groupes/${gid}`);
    setDetail(data);
    setEducIds(data.educateur_ids || []);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isNew) reloadDetail(group.id); }, []);

  const saveMeta = async () => {
    if (!form.nom.trim()) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/groupes', { ...form, educateur_ids: educIds });
        toast.success('Groupe créé');
        onSaved();
      } else {
        await api.patch(`/groupes/${group.id}`, { ...form, educateur_ids: educIds });
        toast.success('Groupe mis à jour');
        onSaved();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const toggleEduc = (id) => setEducIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const assignedIds = new Set((detail?.joueurs || []).map((j) => j.id));
  const available = joueurs.filter((j) => !assignedIds.has(j.id));

  const addJoueur = async (uid) => {
    try { await api.post(`/groupes/${group.id}/joueurs`, { user_id: uid }); await reloadDetail(group.id); }
    catch { toast.error('Ajout impossible'); }
  };
  const removeJoueur = async (uid) => {
    try { await api.delete(`/groupes/${group.id}/joueurs/${uid}`); await reloadDetail(group.id); }
    catch { toast.error('Retrait impossible'); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[#0F172A]/70 backdrop-blur-md grid place-items-center p-3 sm:p-6 overflow-y-auto" onClick={onClose} data-testid="groupe-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-6" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-[#F1F5F9] flex items-center justify-between">
          <h2 className="font-display text-2xl font-black text-[#0F172A]">{isNew ? 'Nouveau groupe' : `Gérer « ${group.nom} »`}</h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]" data-testid="groupe-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Infos */}
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nom du groupe *"><input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="input-base" data-testid="groupe-nom" /></Field>
            <Field label="Catégorie"><input value={form.categorie} onChange={(e) => setForm({ ...form, categorie: e.target.value })} placeholder="U11, Loisir…" className="input-base" data-testid="groupe-categorie" /></Field>
            <Field label="Jour"><input value={form.jour} onChange={(e) => setForm({ ...form, jour: e.target.value })} placeholder="Mercredi" className="input-base" data-testid="groupe-jour" /></Field>
            <Field label="Heure"><input value={form.heure} onChange={(e) => setForm({ ...form, heure: e.target.value })} placeholder="14h00" className="input-base" data-testid="groupe-heure" /></Field>
            <Field label="Lieu"><input value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} placeholder="Gymnase central" className="input-base sm:col-span-2" data-testid="groupe-lieu" /></Field>
          </div>

          {/* Éducateurs */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Éducateurs</div>
            {educs.length === 0 ? (
              <p className="text-xs text-[#94A3B8]">Aucun éducateur. Créez-en un via « Créer un membre ».</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="groupe-educ-picker">
                {educs.map((e) => {
                  const on = educIds.includes(e.id);
                  return (
                    <button key={e.id} onClick={() => toggleEduc(e.id)} className="text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-all inline-flex items-center gap-1.5"
                      style={on ? { background: 'var(--theme-primary)', borderColor: 'var(--theme-primary)', color: '#fff' } : { borderColor: '#E2E8F0', color: '#475569' }}
                      data-testid={`educ-toggle-${e.id}`}>
                      {on && <Check className="w-3 h-3" />} {e.prenom} {e.nom}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Joueurs (édition d'un groupe existant) */}
          {!isNew && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Joueurs du groupe</div>
              {!detail ? <Loader2 className="w-4 h-4 animate-spin text-[#94A3B8]" /> : (
                <>
                  <div className="flex flex-wrap gap-2 mb-3" data-testid="groupe-joueurs">
                    {detail.joueurs.length === 0 && <span className="text-xs text-[#94A3B8]">Aucun joueur affecté.</span>}
                    {detail.joueurs.map((j) => (
                      <span key={j.id} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#F1F5F9] rounded-full pl-3 pr-1.5 py-1.5" data-testid={`joueur-chip-${j.id}`}>
                        {j.prenom} {j.nom}
                        <button onClick={() => removeJoueur(j.id)} className="text-[#94A3B8] hover:text-[#EF4444]" data-testid={`remove-joueur-${j.id}`}><X className="w-3.5 h-3.5" /></button>
                      </span>
                    ))}
                  </div>
                  {available.length > 0 && (
                    <select onChange={(e) => { if (e.target.value) { addJoueur(e.target.value); e.target.value = ''; } }} className="input-base" data-testid="add-joueur-select" defaultValue="">
                      <option value="">+ Ajouter un joueur…</option>
                      {available.map((j) => <option key={j.id} value={j.id}>{j.prenom} {j.nom}</option>)}
                    </select>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#F1F5F9] flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Fermer</button>
          <button onClick={saveMeta} disabled={saving} className="btn-primary" data-testid="groupe-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {isNew ? 'Créer le groupe' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────── Parents ↔ enfants ───────────────
function ParentsSection({ parents, joueurs, onChange }) {
  const [linking, setLinking] = useState(false);
  const [pid, setPid] = useState('');
  const [cid, setCid] = useState('');

  const link = async () => {
    if (!pid || !cid) { toast.error('Sélectionnez un parent et un enfant'); return; }
    try { await api.post('/groupes/parents/link', { parent_id: pid, enfant_id: cid }); toast.success('Enfant rattaché'); setPid(''); setCid(''); setLinking(false); onChange(); }
    catch { toast.error('Rattachement impossible'); }
  };
  const unlink = async (parent_id, enfant_id) => {
    try { await api.post('/groupes/parents/unlink', { parent_id, enfant_id }); toast.success('Détaché'); onChange(); }
    catch { toast.error('Erreur'); }
  };

  return (
    <div className="mt-10" data-testid="parents-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl text-[#0F172A] flex items-center gap-2"><Baby className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} /> Parents & enfants</h2>
        <button onClick={() => setLinking(true)} className="btn-secondary" data-testid="link-parent-btn"><Link2 className="w-4 h-4" /> Rattacher un enfant</button>
      </div>

      {parents.length === 0 ? (
        <p className="text-sm text-[#94A3B8]">Aucun parent enregistré. Créez-en un via « Créer un membre ».</p>
      ) : (
        <div className="bg-white border-2 border-[#E2E8F0] rounded-2xl divide-y divide-[#F1F5F9]">
          {parents.map((p) => (
            <div key={p.id} className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`parent-row-${p.id}`}>
              <div>
                <div className="font-semibold text-[#0F172A]">{p.prenom} {p.nom}</div>
                <div className="text-xs text-[#94A3B8]">{p.email}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {p.enfants.length === 0 && <span className="text-xs text-[#94A3B8]">Aucun enfant rattaché</span>}
                {p.enfants.map((c) => (
                  <span key={c.id} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#F1F5F9] rounded-full pl-3 pr-1.5 py-1.5">
                    {c.prenom} {c.nom}
                    <button onClick={() => unlink(p.id, c.id)} className="text-[#94A3B8] hover:text-[#EF4444]" data-testid={`unlink-${p.id}-${c.id}`}><X className="w-3.5 h-3.5" /></button>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {linking && (
        <div className="fixed inset-0 z-[80] bg-[#0F172A]/70 backdrop-blur-md grid place-items-center p-4" onClick={() => setLinking(false)} data-testid="link-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-black text-xl text-[#0F172A]">Rattacher un enfant</h3>
              <button onClick={() => setLinking(false)} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <Field label="Parent">
                <select value={pid} onChange={(e) => setPid(e.target.value)} className="input-base" data-testid="link-parent-select">
                  <option value="">Choisir un parent…</option>
                  {parents.map((p) => <option key={p.id} value={p.id}>{p.prenom} {p.nom}</option>)}
                </select>
              </Field>
              <Field label="Enfant (joueur)">
                <select value={cid} onChange={(e) => setCid(e.target.value)} className="input-base" data-testid="link-enfant-select">
                  <option value="">Choisir un joueur…</option>
                  {joueurs.map((j) => <option key={j.id} value={j.id}>{j.prenom} {j.nom}</option>)}
                </select>
              </Field>
            </div>
            <button onClick={link} className="btn-primary w-full mt-5" data-testid="link-submit"><Link2 className="w-4 h-4" /> Rattacher</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────── Création rapide d'un membre ───────────────
function QuickMemberModal({ onClose, onSaved }) {
  const [role, setRole] = useState('joueur');
  const [form, setForm] = useState({ prenom: '', nom: '', email: '', telephone: '' });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async () => {
    if (!form.prenom.trim() || !form.email.trim()) { toast.error('Prénom et email requis'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/groupes/membres', { ...form, role });
      setCreated(data);
      toast.success(`${ROLE_LABELS[role]} créé`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[#0F172A]/70 backdrop-blur-md grid place-items-center p-4" onClick={onClose} data-testid="quick-member-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-black text-xl text-[#0F172A]">Créer un membre</h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        </div>

        {created ? (
          <div className="text-center py-4" data-testid="quick-member-created">
            <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-3 text-white" style={{ background: 'var(--theme-primary)' }}><Check className="w-7 h-7" /></div>
            <p className="font-semibold text-[#0F172A]">{created.prenom} {created.nom} — {ROLE_LABELS[created.role]}</p>
            <p className="text-sm text-[#475569] mt-2">Identifiants à transmettre :</p>
            <div className="bg-[#F9F9F8] rounded-xl p-3 mt-2 text-sm font-mono text-left">
              <div>Email : <strong>{created.email}</strong></div>
              <div>Mot de passe : <strong>{created.mot_de_passe_temporaire}</strong></div>
            </div>
            <button onClick={onSaved} className="btn-primary w-full mt-5">Terminé</button>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-4">
              {Object.entries(ROLE_LABELS).map(([k, label]) => (
                <button key={k} onClick={() => setRole(k)} className="flex-1 text-sm font-bold px-3 py-2 rounded-lg border-2 transition-all"
                  style={role === k ? { background: 'var(--theme-primary)', borderColor: 'var(--theme-primary)', color: '#fff' } : { borderColor: '#E2E8F0', color: '#475569' }}
                  data-testid={`role-${k}`}>{label}</button>
              ))}
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Prénom *"><input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} className="input-base" data-testid="qm-prenom" /></Field>
                <Field label="Nom"><input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="input-base" data-testid="qm-nom" /></Field>
              </div>
              <Field label="Email *"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-base" data-testid="qm-email" /></Field>
              <Field label="Téléphone"><input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} className="input-base" data-testid="qm-tel" /></Field>
            </div>
            <button onClick={submit} disabled={saving} className="btn-primary w-full mt-5" data-testid="qm-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Créer le {ROLE_LABELS[role].toLowerCase()}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
